import React, { useState, useEffect } from 'react';
import { useUser } from '../../hooks/useUser';
import { useBackHandler } from '../../hooks/useBackHandler';

const VIDEO_URL = 'https://hive-fn.qpyou.cn/markup/img/brand/smon_v2/event/11th_anniversary/main_video_v2.mp4';
const VIDEO_CACHE_NAME = 'login-video-cache';

// 영상 캐싱: 최초 1회 다운로드 후 브라우저 Cache API에 저장
const getCachedVideoUrl = async (): Promise<string> => {
  try {
    const cache = await caches.open(VIDEO_CACHE_NAME);
    const cached = await cache.match(VIDEO_URL);
    if (cached) {
      const blob = await cached.blob();
      return URL.createObjectURL(blob);
    }
    // 캐시에 없으면 다운로드 후 저장
    const response = await fetch(VIDEO_URL);
    if (response.ok) {
      cache.put(VIDEO_URL, response.clone());
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    }
  } catch (e) {
    console.warn('[VideoCache] 캐싱 실패, 원본 URL 사용:', e);
  }
  return VIDEO_URL;
};

interface LoginProps {
  onLogin: (uuid: string) => void;
  checkingAuth?: boolean;
}

const Login: React.FC<LoginProps> = ({ onLogin, checkingAuth }) => {
  const [enteredId, setEnteredId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showJoin, setShowJoin] = useState(false);
  const [joinUuid, setJoinUuid] = useState('');
  const [joinNick, setJoinNick] = useState('');
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinMsg, setJoinMsg] = useState('');
  const [videoSrc, setVideoSrc] = useState(VIDEO_URL);

  useEffect(() => {
    getCachedVideoUrl().then(setVideoSrc);
    return () => {
      // blob URL 해제
      if (videoSrc.startsWith('blob:')) URL.revokeObjectURL(videoSrc);
    };
  }, []);
  
  const { handleLogin, handleJoin } = useUser();

  // 뒤로 가기: 가입 요청 화면 → 로그인 화면 ([로그인으로 돌아가기] 와 같다)
  useBackHandler(!checkingAuth && showJoin, () => { setShowJoin(false); setJoinMsg(''); });

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await handleLogin(enteredId);
    if (result.success) {
      onLogin(enteredId);
    } else {
      setError(result.error || '로그인 실패');
    }
    setLoading(false);
  };

  const handleJoinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setJoinLoading(true);
    setJoinMsg('');
    const result = await handleJoin(joinUuid, joinNick);
    if (result.success) {
      setJoinMsg('가입 요청이 접수되었습니다. 관리자의 승인을 기다려주세요.');
      setJoinUuid('');
      setJoinNick('');
    } else {
      setJoinMsg(result.error || '가입 요청 실패');
    }
    setJoinLoading(false);
  };

  return (
    <>
      <video
        src={videoSrc}
        autoPlay
        loop
        muted
        playsInline
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          width: '100vw',
          height: '100vh',
          objectFit: 'cover',
          zIndex: -1,
          pointerEvents: 'none',
        }}
      />
      <div style={{
        position: 'fixed',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        maxWidth: 340,
        width: '90%',
        background: 'rgba(20,20,20,0.82)',
        borderRadius: 16,
        boxShadow: '0 4px 24px #0008',
        padding: 32,
        color: '#fff',
        zIndex: 1
      }}>
        <h2 style={{ color: '#fff', fontWeight: 700, fontSize: 26, marginBottom: 29, letterSpacing: -1, textShadow: '0 2px 8px #0008' }}>로그인</h2>
        {checkingAuth ? (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{
              display: 'inline-block',
              width: 32,
              height: 32,
              border: '3px solid rgba(255,255,255,0.2)',
              borderTop: '3px solid #90caf9',
              borderRadius: '50%',
              animation: 'spin 0.9s linear infinite',
              marginBottom: 14
            }} />
            <div style={{ color: '#90caf9', fontSize: 15, fontWeight: 600 }}>자동 로그인 중...</div>
          </div>
        ) : !showJoin ? (
          <>
            <form onSubmit={handleLoginSubmit} style={{ width: '100%', maxWidth: 320, margin: '0 auto' }}>
              <input
                type="text"
                value={enteredId}
                onChange={e => setEnteredId(e.target.value)}
                placeholder="ID를 입력하세요"
                style={{ width: '100%', padding: '12px 14px', fontSize: 16, borderRadius: 8, border: '1px solid #444', marginBottom: 16, outline: 'none', boxSizing: 'border-box', background: 'rgba(0,0,0,0.18)', color: '#fff' }}
                disabled={loading}
              />
              <button
                type="submit"
                className="login-btn"
                disabled={loading || !enteredId}
              >
                {loading ? '로그인 중...' : '로그인'}
              </button>
              {error && <div style={{ color: '#d32f2f', marginTop: 8, fontSize: 15 }}>{error}</div>}
            </form>
            <button
              onClick={() => setShowJoin(true)}
              style={{ width: '100%', marginTop: 12, background: 'none', border: 'none', color: '#90caf9', fontWeight: 700, fontSize: 15, cursor: 'pointer', textDecoration: 'underline' }}
            >
              회원가입
            </button>
          </>
        ) : (
          <>
            <form onSubmit={handleJoinSubmit} style={{ width: '100%', maxWidth: 320, margin: '0 auto' }}>
              <input
                type="text"
                value={joinUuid}
                onChange={e => setJoinUuid(e.target.value)}
                placeholder="ID (영문/숫자)"
                style={{ width: '100%', padding: '12px 14px', fontSize: 16, borderRadius: 8, border: '1px solid #444', marginBottom: 12, outline: 'none', boxSizing: 'border-box', background: 'rgba(0,0,0,0.18)', color: '#fff' }}
                disabled={joinLoading}
              />
              <input
                type="text"
                value={joinNick}
                onChange={e => setJoinNick(e.target.value)}
                placeholder="닉네임"
                style={{ width: '100%', padding: '12px 14px', fontSize: 16, borderRadius: 8, border: '1px solid #444', marginBottom: 16, outline: 'none', boxSizing: 'border-box', background: 'rgba(0,0,0,0.18)', color: '#fff' }}
                disabled={joinLoading}
              />
              <button
                type="submit"
                style={{ width: '100%', padding: '12px 0', background: '#222', color: '#fff', fontWeight: 700, fontSize: 17, border: 'none', borderRadius: 8, cursor: 'pointer', marginBottom: 8, transition: 'background 0.2s' }}
                disabled={joinLoading || !joinUuid || !joinNick}
              >
                {joinLoading ? '가입 요청 중...' : '가입 요청'}
              </button>
              {joinMsg && <div style={{ color: joinMsg.includes('접수') ? '#1976d2' : '#d32f2f', marginTop: 8, fontSize: 15, whiteSpace: 'pre-line' }}>{joinMsg}</div>}
            </form>
            <button
              onClick={() => { setShowJoin(false); setJoinMsg(''); }}
              style={{ width: '100%', marginTop: 12, background: 'none', border: 'none', color: '#bbb', fontWeight: 700, fontSize: 15, cursor: 'pointer', textDecoration: 'underline' }}
            >
              로그인으로 돌아가기
            </button>
          </>
        )}
      </div>
      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          .login-btn {
            width: 100%;
            padding: 12px 0;
            background: #222;
            color: #fff;
            font-weight: 700;
            font-size: 17px;
            border: none;
            border-radius: 8px;
            cursor: pointer;
            margin-bottom: 8px;
            transition: background 0.2s;
          }
          .login-btn:hover {
            background: #444;
          }
        `}
      </style>
    </>
  );
};

export default Login;